const formatTemp = (unit) => {
  if (!unit && unit !== 0) return unit;
  const lower = unit.toString().trim().toLowerCase();
  if (/^deg\s*c$/.test(lower) || /celsius/i.test(lower) || /^degc$/i.test(lower)) return 'C';
  if (/^deg\s*f$/.test(lower) || /fahrenheit/i.test(lower) || /^degf$/i.test(lower)) return 'F';
  return unit;
};

/**
 * Normalize USGS measurement values into a display unit and optional alternate unit.
 *
 * @param {number} value
 * @param {string} unit
 * @returns {{value:number,unit:string|null,alt_value:number|null,alt_unit:string|null}|null}
 */
export const USGSMeasureNormalizer = (value, unit) => {
  if (value == null || !isFinite(Number(value))) return null;
  if (unit == null) return { value: Number(value), unit: null };

  let normalizeUnit = unit;
  let altValue = null;
  let altUnit = null;
  const v = Number(value);
  const u = (unit || '').toString().trim().toLowerCase();

  switch (true) {
    case /(deg\s*c|celsius|c$)/i.test(u):
      normalizeUnit = 'C';
      altValue = roundToHundredths((v * 9) / 5 + 32);
      altUnit = 'F';
      break;
    case /(deg\s*f|fahrenheit|f$)/i.test(u):
      normalizeUnit = 'F';
      altValue = roundToHundredths(((v - 32) * 5) / 9);
      altUnit = 'C';
      break;
    case /^(ft|feet?)$/.test(u):
      normalizeUnit = 'ft';
      altValue = roundToHundredths(v * 12);
      altUnit = 'in';
      break;
    case /^(in|inch|inches)$/.test(u):
      normalizeUnit = 'in';
      altValue = roundToHundredths(v / 12);
      altUnit = 'ft';
      break;
    case /^(yd|yard|yards)$/.test(u):
      normalizeUnit = 'yd';
      altValue = roundToHundredths(v * 0.9144);
      altUnit = 'm';
      break;
    case /(mi|mile|miles)$/.test(u):
      normalizeUnit = 'mi';
      altValue = roundToHundredths(v * 1.609344);
      altUnit = 'km';
      break;
    case /^(m|meter|metre|meters|metres)$/.test(u):
      normalizeUnit = 'm';
      altValue = roundToHundredths(v / 0.3048);
      altUnit = 'ft';
      break;
    case /^(cm|centimeter|centimetre|centimeters|centimetres)$/.test(u):
      normalizeUnit = 'cm';
      altValue = roundToHundredths(v / 2.54);
      altUnit = 'in';
      break;
    case /^(km|kilometer|kilometre|kilometers|kilometres)$/.test(u):
      normalizeUnit = 'km';
      altValue = roundToHundredths(v / 1.609344);
      altUnit = 'mi';
      break;
    case /^(psi)$/.test(u):
      normalizeUnit = 'psi';
      altValue = roundToHundredths(v * 6.89475729);
      altUnit = 'kPa';
      break;
    case /^(kpa|kilopascal|kilopascals)$/.test(u):
      normalizeUnit = 'kpa';
      altValue = roundToHundredths(v / 6.89475729);
      altUnit = 'psi';
      break;
    case /^cfs$/i.test(u) || /cubic\s*feet\s*per\s*second/i.test(u):
      normalizeUnit = 'ft^3/s';
      break;
    default:
      normalizeUnit = unit;
      break;
  }

  return {
    value: Number(value),
    unit: formatTemp(normalizeUnit),
    alt_value: altValue,
    alt_unit: altUnit,
  };
};

export const roundToHundredths = (num) => {
  if (num == null || !isFinite(Number(num))) return num;
  return Math.round(num * 100) / 100;
};
